import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BACKGROUND_REFETCH_MS, queryKeys } from '@/api/queryKeys'
import * as bucketsApi from '@/api/buckets'
import type { Bucket } from '@/types'

/** All active (non-archived) buckets, sorted by rank ascending. */
export function useBuckets(): Bucket[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.buckets.list('active'),
    queryFn: () => bucketsApi.fetchBuckets('active') as Promise<Bucket[]>,
    refetchOnWindowFocus: true,
    refetchInterval: BACKGROUND_REFETCH_MS,
  })
  return data
}

/** Active user-owned buckets (non-system), sorted by rank. */
export function useUserBuckets(): Bucket[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.buckets.list('user'),
    queryFn: () => bucketsApi.fetchBuckets('user') as Promise<Bucket[]>,
    refetchOnWindowFocus: true,
    refetchInterval: BACKGROUND_REFETCH_MS,
  })
  return data
}

/** All archived non-system buckets, newest-archived first. */
export function useArchivedBuckets(): Bucket[] | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.buckets.list('archived'),
    queryFn: () => bucketsApi.fetchBuckets('archived') as Promise<Bucket[]>,
    refetchOnWindowFocus: true,
  })
  return data
}

/** The permanent Unsorted system bucket. */
export function useSystemBucket(): Bucket | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.buckets.list('system'),
    queryFn: () => bucketsApi.fetchBuckets('system') as Promise<Bucket | null>,
    refetchOnWindowFocus: true,
  })
  return data ?? undefined
}

function invalidateBuckets(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['buckets'] })
}

export function useCreateBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: bucketsApi.createBucket,
    onSuccess: () => invalidateBuckets(qc),
  })
}

export function useUpdateBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof bucketsApi.updateBucket>[1] }) =>
      bucketsApi.updateBucket(id, data),
    onSuccess: () => invalidateBuckets(qc),
  })
}

export function useReorderBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, beforeRank, afterRank }: { id: string; beforeRank: number | null; afterRank: number | null }) =>
      bucketsApi.reorderBucket(id, beforeRank, afterRank),
    onSuccess: () => invalidateBuckets(qc),
  })
}

export function useArchiveBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, priorityDisposition }: { id: string; priorityDisposition: string }) =>
      bucketsApi.archiveBucket(id, priorityDisposition),
    onSuccess: () => {
      invalidateBuckets(qc)
      void qc.invalidateQueries({ queryKey: ['priorities'] })
    },
  })
}

export function useRestoreBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: bucketsApi.restoreBucket,
    onSuccess: () => invalidateBuckets(qc),
  })
}

export function useDeleteBucket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: bucketsApi.permanentlyDeleteBucket,
    onSuccess: () => invalidateBuckets(qc),
  })
}
